import { buildPageLayoutMetrics } from 'features/knowledgeDocumentViewer/components/viewer/continuous/pageLayoutMetrics'
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

const SUPPRESS_DURATION_MS = 400
const SMOOTH_SCROLL_MAX_VIEWPORTS = 2
const SMOOTH_SCROLL_MAX_PAGES = 4

function findPageAtScroll(
  scrollY: number,
  pageTops: number[],
  pageHeights: number[],
  pageGap: number
): number {
  let low = 0
  let high = pageTops.length - 1
  let bestPage = pageTops.length

  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    const page = mid + 1
    const pageEnd = pageTops[mid] + (pageHeights[mid] ?? 0) + pageGap / 2
    if (scrollY < pageEnd) {
      bestPage = page
      high = mid - 1
    } else {
      low = mid + 1
    }
  }

  return bestPage
}

export function useContinuousScrollSync(
  scrollContainerRef: RefObject<HTMLDivElement | null>,
  totalPages: number,
  getPageHeight: (page: number) => number,
  pageGap: number,
  initialPage: number,
  onPageChange: (page: number) => void
) {
  const [currentPage, setCurrentPage] = useState(initialPage)
  const currentPageRef = useRef(initialPage)
  const [loadedRange, setLoadedRange] = useState({
    start: Math.max(1, initialPage - 2),
    end: Math.min(totalPages, initialPage + 4),
  })
  const isInitialScrollDone = useRef(false)
  const suppressUntil = useRef(0)
  const lastReportedPage = useRef(initialPage)

  const pageLayoutMetrics = useMemo(
    () => buildPageLayoutMetrics(totalPages, getPageHeight, pageGap),
    [totalPages, getPageHeight, pageGap]
  )

  const ready = getPageHeight(1) > 0

  const scrollToPage = useCallback(
    (page: number, smooth = true) => {
      const container = scrollContainerRef.current
      if (!container || !ready) return
      const targetScrollTop = pageLayoutMetrics.tops[page - 1] ?? 0
      const pageDistance = Math.abs(page - currentPageRef.current)
      const scrollDistance = Math.abs(targetScrollTop - container.scrollTop)
      const maxSmoothScrollDistance =
        container.clientHeight * SMOOTH_SCROLL_MAX_VIEWPORTS
      const shouldSmoothScroll =
        smooth &&
        pageDistance <= SMOOTH_SCROLL_MAX_PAGES &&
        scrollDistance <= maxSmoothScrollDistance

      suppressUntil.current =
        Date.now() + (shouldSmoothScroll ? SUPPRESS_DURATION_MS : 50)
      container.scrollTo({
        top: targetScrollTop,
        behavior: shouldSmoothScroll ? 'smooth' : 'instant',
      })
      currentPageRef.current = page
      setCurrentPage(page)
      const overscan = 3
      setLoadedRange({
        start: Math.max(1, page - overscan),
        end: Math.min(totalPages, page + overscan),
      })
    },
    [scrollContainerRef, ready, pageLayoutMetrics, totalPages]
  )

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container || !ready) return

    let rafId: number | null = null
    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    const handleScroll = () => {
      if (rafId !== null) return
      rafId = requestAnimationFrame(() => {
        rafId = null

        if (Date.now() < suppressUntil.current) return

        const scrollTop = container.scrollTop
        const viewportCenter = scrollTop + container.clientHeight / 2
        const centerPage = Math.max(
          1,
          Math.min(
            totalPages,
            findPageAtScroll(
              viewportCenter,
              pageLayoutMetrics.tops,
              pageLayoutMetrics.heights,
              pageGap
            )
          )
        )

        if (centerPage !== currentPageRef.current) {
          currentPageRef.current = centerPage
          setCurrentPage(centerPage)
          const overscan = 3
          setLoadedRange({
            start: Math.max(1, centerPage - overscan),
            end: Math.min(totalPages, centerPage + overscan),
          })

          if (debounceTimer) clearTimeout(debounceTimer)
          debounceTimer = setTimeout(() => {
            lastReportedPage.current = centerPage
            onPageChange(centerPage)
          }, 150)
        }
      })
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      container.removeEventListener('scroll', handleScroll)
      if (rafId !== null) cancelAnimationFrame(rafId)
      if (debounceTimer) clearTimeout(debounceTimer)
    }
  }, [
    scrollContainerRef,
    ready,
    totalPages,
    pageLayoutMetrics,
    pageGap,
    onPageChange,
  ])

  useEffect(() => {
    if (isInitialScrollDone.current || !ready) return
    scrollToPage(initialPage, false)
    isInitialScrollDone.current = true
  }, [ready, initialPage, scrollToPage])

  const handleExternalPageChange = useCallback(
    (page: number) => {
      if (page === currentPageRef.current) return
      if (page === lastReportedPage.current) return
      scrollToPage(page)
    },
    [scrollToPage]
  )

  return { currentPage, loadedRange, handleExternalPageChange }
}
